"use client";

import Link from "next/link";
import { Fragment, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { PublicProductFilterOptionsDto, PublicUsageAreaDto } from "@/lib/api-client";
import { filterButtonAttributes, resolveFilterLayout, type FilterKey, type FilterLayout } from "@/lib/filter-registry";
import type { FilterButtonDesign } from "@/lib/theme-settings";
import { createTranslator } from "@/lib/localization";

type Lookups = {
  filterOptions: PublicProductFilterOptionsDto;
  usageAreas: PublicUsageAreaDto[];
};

type Status = "idle" | "loading" | "loaded" | "error";

type Props = {
  activeLanguage: string;
  texts?: Record<string, string> | null;
  /** Href of the CMS page (in the active language) that renders the shop filter
   * template, resolved server-side from the PageTranslation relationship — see
   * HomePageView's use of apiClient.getPageSlugByToken. Never a hardcoded slug. */
  shopPageHref: string;
  /** Which registry filters render, in order — ALREADY resolved and sanitized on the server from the domain's
   * public filter configuration (lib/filter-registry.ts), so no raw configuration ever reaches the browser.
   * Undefined = the registry defaults = exactly the filters this panel always showed. It only decides which
   * CONTROLS render; the submitted query is built from whatever controls exist, as before. */
  filterLayout?: FilterLayout;
  /** Colors of the PRIMARY action ("Show Products") only — validated, or nothing. */
  buttonDesign?: FilterButtonDesign;
};

export function HomeProductFilterPanel({ activeLanguage, texts, shopPageHref, filterLayout, buttonDesign }: Props) {
  const targetHref = shopPageHref;
  // effective domain configuration ∩ Home-supported filters → enabled → ordered (lib/filter-registry.ts)
  const layout = filterLayout ?? resolveFilterLayout("home", null);
  const tr = createTranslator(texts ?? {});
  const t = {
    title: tr("home.productFilter.title"),
    mobileButton: tr("home.productFilter.mobileButton"),
    showProducts: tr("home.productFilter.showProducts"),
    clear: tr("home.productFilter.clear"),
    advancedFilters: tr("home.productFilter.advancedFilters"),
    searchPlaceholder: tr("home.productFilter.searchPlaceholder"),
    category: tr("home.productFilter.category"),
    usageArea: tr("home.productFilter.usageArea"),
    usageAreaAll: tr("home.productFilter.usageAreaAll"),
    color: tr("home.productFilter.color"),
    priceRange: tr("home.productFilter.priceRange"),
    priceMin: tr("home.productFilter.priceMin"),
    priceMax: tr("home.productFilter.priceMax"),
    size: tr("home.productFilter.size"),
    loading: tr("home.productFilter.loading"),
    error: tr("home.productFilter.error"),
  };

  const [status, setStatus] = useState<Status>("idle");
  const [lookups, setLookups] = useState<Lookups | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    fetchedRef.current = false;
    setStatus("idle");
    setLookups(null);

    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(min-width: 1024px)");

    const maybeFetch = () => {
      // Only ever fetch once per mount, and only once desktop width is confirmed —
      // mobile viewports never match, so no lookup call fires there at all.
      if (!mql.matches || fetchedRef.current) return;
      fetchedRef.current = true;
      setStatus("loading");

      Promise.all([
        fetch("/api/public/products/filters", {
          headers: { "x-active-language": activeLanguage },
        }).then((r) => {
          if (!r.ok) throw new Error("filters request failed");
          return r.json() as Promise<PublicProductFilterOptionsDto>;
        }),
        fetch(`/api/public/suitability/usage-areas?lang=${encodeURIComponent(activeLanguage)}`).then((r) => {
          if (!r.ok) throw new Error("usage-areas request failed");
          return r.json() as Promise<PublicUsageAreaDto[]>;
        }),
      ])
        .then(([filterOptions, usageAreas]) => {
          setLookups({ filterOptions, usageAreas });
          setStatus("loaded");
        })
        .catch(() => setStatus("error"));
    };

    maybeFetch();
    mql.addEventListener("change", maybeFetch);
    return () => mql.removeEventListener("change", maybeFetch);
  }, [activeLanguage]);

  // One renderer per Home-supported registry key. The markup is the panel's original markup, moved as-is.
  const renderFilter = (key: FilterKey, data: Lookups): ReactNode => {
    switch (key) {
      case "productName":
        return (
          <div className="rounded-lg border border-gray-3 bg-white p-3">
            <input
              type="text"
              name="productName"
              placeholder={t.searchPlaceholder}
              className="w-full rounded-md border border-gray-3 bg-white px-3 py-2 text-sm"
            />
          </div>
        );

      case "category":
        return (
          data.filterOptions.categories.length > 0 && (
            <div className="rounded-lg border border-gray-3 bg-white p-3">
              <h3 className="text-sm font-semibold text-dark mb-2">{t.category}</h3>
              <div className="space-y-2 max-h-40 overflow-auto pr-1">
                {data.filterOptions.categories.map((category) => (
                  <label
                    key={category.slug}
                    htmlFor={`home-cat-${category.slug}`}
                    className="flex items-center gap-2 text-sm text-dark-2 cursor-pointer hover:text-dark"
                  >
                    <input type="checkbox" id={`home-cat-${category.slug}`} name="categories" value={category.slug} />
                    <span className="truncate">{category.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )
        );

      case "usageArea":
        return (
          <div className="rounded-lg border border-gray-3 bg-white p-3">
            <h3 className="text-sm font-semibold text-dark mb-2">{t.usageArea}</h3>
            <label htmlFor="home-usageArea-select" className="sr-only">
              {t.usageArea}
            </label>
            <select
              id="home-usageArea-select"
              name="usageArea"
              defaultValue=""
              className="w-full rounded-md border border-gray-3 bg-white px-3 py-2 text-sm"
            >
              <option value="">{t.usageAreaAll}</option>
              {data.usageAreas.map((area) => (
                <option key={area.code} value={area.code}>
                  {area.name}
                </option>
              ))}
            </select>
          </div>
        );

      case "color":
        return (
          data.filterOptions.colors.length > 0 && (
            <div className="rounded-lg border border-gray-3 bg-white p-3">
              <h3 className="text-sm font-semibold text-dark mb-2">{t.color}</h3>
              <div className="space-y-2 max-h-40 overflow-auto pr-1">
                {data.filterOptions.colors.map((color) => (
                  <label
                    key={color.id}
                    htmlFor={`home-color-${color.id}`}
                    className="flex items-center gap-2 text-sm text-dark-2 cursor-pointer hover:text-dark"
                  >
                    <input type="checkbox" id={`home-color-${color.id}`} name="colorIds" value={color.id} />
                    <span className="truncate">{color.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )
        );

      case "price":
        return (
          <div className="rounded-lg border border-gray-3 bg-white p-3">
            <h3 className="text-sm font-semibold text-dark mb-2">{t.priceRange}</h3>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                name="minPrice"
                placeholder={`${t.priceMin} ${Math.floor(data.filterOptions.minPrice)}`}
                className="w-full rounded-md border border-gray-3 bg-white px-3 py-2 text-sm"
              />
              <input
                type="number"
                name="maxPrice"
                placeholder={`${t.priceMax} ${Math.ceil(data.filterOptions.maxPrice)}`}
                className="w-full rounded-md border border-gray-3 bg-white px-3 py-2 text-sm"
              />
            </div>
          </div>
        );

      case "size":
        return (
          data.filterOptions.sizes.length > 0 && (
            <div className="rounded-lg border border-gray-3 bg-white p-3">
              <h3 className="text-sm font-semibold text-dark mb-2">{t.size}</h3>
              <div className="space-y-2 max-h-40 overflow-auto pr-1">
                {data.filterOptions.sizes.map((size) => (
                  <label
                    key={size.slug}
                    htmlFor={`home-size-${size.slug}`}
                    className="flex items-center gap-2 text-sm text-dark-2 cursor-pointer hover:text-dark"
                  >
                    <input type="checkbox" id={`home-size-${size.slug}`} name="sizes" value={size.slug} />
                    <span className="truncate">{size.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )
        );

      // Shop-only registry keys (subcategory, region, technicalProperties) have no Home control: the layout
      // never contains them, and if one ever reached here it renders nothing.
      default:
        return null;
    }
  };

  const submitButton = filterButtonAttributes(
    buttonDesign,
    "rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark transition-colors",
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(e.currentTarget)) {
      if (value) params.append(key, String(value));
    }
    const qs = params.toString();
    window.location.href = qs ? `${targetHref}?${qs}` : targetHref;
  };

  return (
    <>
      {/* Mobile: link-only, never triggers a lookup fetch */}
      <div className="lg:hidden mb-6">
        <Link
          href={targetHref}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-dark transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M3 2H17C17.55 2 18 2.45 18 3C18 3.55 17.55 4 17 4H3C2.45 4 2 3.55 2 3C2 2.45 2.45 2 3 2Z" fill="currentColor" />
            <path d="M4 9H16C16.55 9 17 9.45 17 10C17 10.55 16.55 11 16 11H4C3.45 11 3 10.55 3 10C3 9.45 3.45 9 4 9Z" fill="currentColor" />
            <path d="M5 16H15C15.55 16 16 16.45 16 17C16 17.55 15.55 18 15 18H5C4.45 18 4 17.55 4 17C4 16.45 4.45 16 5 16Z" fill="currentColor" />
          </svg>
          {t.mobileButton}
        </Link>
      </div>

      {/* Desktop: shell is always in the DOM (CSS hidden below lg), inner content is
          populated only after the client-side fetch resolves -> no hydration mismatch */}
      <aside className="hidden lg:block rounded-xl border border-gray-3 bg-gray-1 p-4 lg:sticky lg:top-24 lg:h-fit">
        <form method="get" onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center justify-between px-4 py-3 bg-white rounded-lg shadow-1">
            <p className="text-sm font-semibold text-dark">{t.title}</p>
            <button type="reset" className="text-blue text-sm font-semibold hover:underline">
              {t.clear}
            </button>
          </div>

          {status === "loaded" && lookups ? (
            <>
              {layout.keys.map((key) => (
                <Fragment key={key}>{renderFilter(key, lookups)}</Fragment>
              ))}
            </>
          ) : (
            <div className="rounded-lg border border-gray-3 bg-white p-4 text-sm text-dark-3">
              {status === "error" ? t.error : t.loading}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button type="submit" className={submitButton.className} style={submitButton.style}>
              {t.showProducts}
            </button>
            <Link
              href={targetHref}
              className="rounded-md border border-gray-3 bg-white px-4 py-2 text-center text-sm font-semibold text-dark hover:bg-gray-2 transition-colors"
            >
              {t.advancedFilters}
            </Link>
          </div>
        </form>
      </aside>
    </>
  );
}
